import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Plan } from "../api/get-plan";
import { useChargebeePlan } from "@/features/plans/api/get-chargebee-plan";

export default function ExternalPlanDetails({ plan }: { plan: Plan }) {
    const { data, isLoading, error } = useChargebeePlan({ planId: plan.planSku || '' })

    if (isLoading) return <div>Loading external plan…</div>
    if (error || !data) return <div className='text-destructive'>Failed to load external plan.</div>

    const price = typeof data.price === 'number' ? (data.price / 100).toFixed(2) : '0.00'
    const isArchived = data.status === 'archived'
    const archivedAt = data.archived_at ? new Date(data.archived_at * 1000) : null

    return (
        <Card>
            <CardHeader>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <CardTitle className='text-2xl'>{data.name || 'External Plan'}</CardTitle>
                        {data.description ? (
                            <p className='text-sm text-muted-foreground mt-1 max-w-prose'>{data.description}</p>
                        ) : null}
                    </div>
                    <div className='flex items-center gap-2'>
                        {data.channel ? <Badge variant='outline'>{data.channel}</Badge> : null}
                        <Badge variant={isArchived ? 'destructive' : 'secondary'} className='capitalize'>{data.status}</Badge>
                    </div>
                </div>
                <CardDescription>
                    <Badge variant='outline'><img src='/chargebee_logo.png' alt='Chargebee' className='h-6' /></Badge>
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                    <div className='rounded-lg border p-4'>
                        <div className='text-xs text-muted-foreground mb-1'>Price</div>
                        <div className='text-3xl font-semibold'>${price}</div>
                        <div className='text-sm text-muted-foreground'>per {data.period} {data.period_unit}{data.period && data.period > 1 ? 's' : ''}</div>
                        <Separator className='my-3' />
                        <div className='text-xs text-muted-foreground mb-1'>Pricing Model</div>
                        <div className='text-sm'>{data.pricing_model || data.charge_model || '—'}</div>
                    </div>

                    <div className='md:col-span-2'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <div className='rounded-lg border p-4'>
                                <div className='text-xs text-muted-foreground mb-1'>Invoice Name</div>
                                <div className='text-sm font-medium'>{data.invoice_name || '—'}</div>
                                <Separator className='my-3' />
                                <div className='grid grid-cols-2 gap-3'>
                                    <div>
                                        <div className='text-xs text-muted-foreground mb-1'>Currency</div>
                                        <div className='text-sm font-medium'>{data.currency_code || 'USD'}</div>
                                    </div>
                                    <div>
                                        <div className='text-xs text-muted-foreground mb-1'>Taxable</div>
                                        <div className='text-sm font-medium'>{data.taxable ? 'Yes' : 'No'}</div>
                                    </div>
                                </div>
                            </div>
                            <div className='rounded-lg border p-4'>
                                <div className='text-xs text-muted-foreground mb-1'>Distribution</div>
                                <div className='grid grid-cols-2 gap-3'>
                                    <div>
                                        <div className='text-xs text-muted-foreground mb-1'>Hosted Pages</div>
                                        <div className='text-sm font-medium'>{data.enabled_in_hosted_pages ? 'Enabled' : 'Disabled'}</div>
                                    </div>
                                    <div>
                                        <div className='text-xs text-muted-foreground mb-1'>Customer Portal</div>
                                        <div className='text-sm font-medium'>{data.enabled_in_portal ? 'Enabled' : 'Disabled'}</div>
                                    </div>
                                    <div className='sm:col-span-2'>
                                        <div className='text-xs text-muted-foreground mb-1'>Add‑on Applicability</div>
                                        <div className='text-sm font-medium capitalize'>{data.addon_applicability || '—'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator className='my-6' />
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div>
                        <div className='text-xs text-muted-foreground mb-1'>Plan ID</div>
                        <div className='font-mono text-sm'>{data.id}</div>
                    </div>
                    <div>
                        <div className='text-xs text-muted-foreground mb-1'>Resource Version</div>
                        <div className='font-mono text-sm'>{data.resource_version ?? '—'}</div>
                    </div>
                    <div>
                        <div className='text-xs text-muted-foreground mb-1'>Updated</div>
                        <div className='text-sm'>{data.updated_at ? new Date(data.updated_at * 1000).toLocaleString() : '—'}</div>
                    </div>
                </div>

                {isArchived && archivedAt ? (
                    <div className='mt-4 text-xs text-muted-foreground'>Archived on {archivedAt.toLocaleDateString()}</div>
                ) : null}
            </CardContent>
        </Card>
    )
}